import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTenantCrud } from '../hooks/useTenantData';
import { useClub } from '../contexts/ClubContext';

function Register() {
  const { club } = useClub();
  const { add } = useTenantCrud('registrations');
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    // Player Info
    playerFirstName: '',
    playerLastName: '',
    dateOfBirth: '',
    gender: '',
    ageGroup: '',
    position: '',
    
    // Parent/Guardian Info
    parentFirstName: '',
    parentLastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    
   // Medical Info
    medicalConditions: '',
    allergies: '',
    priorInjuries: '',
    emergencyContact: '',
    
    // Terms
    agreeTerms: false,
    agreePhotos: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const nextStep = () => {
    setStep(step + 1);
    window.scrollTo(0, 0);
  };

  const prevStep = () => {
    setStep(step - 1);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Add registration using tenant-scoped hook (clubId added automatically)
      const newReg = await add({
        ...formData,
        status: 'pending',
        submittedAt: new Date()
      });
      
      setSubmitted(true);
      console.log('Registration saved:', newReg.id);
    } catch (error) {
      console.error('Error saving registration:', error);
      alert('Error submitting registration. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Success screen after submission
  if (submitted) {
    return (
      <div className="register-page">
        <div className="register-hero">
          <h1>REGISTRATION COMPLETE</h1>
          <p>Thank you for registering with {club?.name || 'ClubLink TT'}!</p>
        </div>
        <div className="form-container">
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h2>Registration Submitted Successfully!</h2>
            <p>Thank you for registering <strong>{formData.playerFirstName} {formData.playerLastName}</strong> with {club?.name || 'ClubLink TT'}.</p>
            <div className="success-details">
              <p><strong>Age Group:</strong> {formData.ageGroup}</p>
              <p><strong>Contact Email:</strong> {formData.email}</p>
            </div>
            <p className="success-note">We will review your registration and contact you at <strong>{formData.email}</strong> within 2-3 business days with next steps.</p>
            <div className="success-fee">
              {/* TODO: Update fee amount when confirmed by club */}
              <h4>Registration Fee: TT$ 200</h4>
              <p>Payment instructions will be sent to your email.</p>
            </div>
            <Link to="/" className="btn-home">BACK TO HOME</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="register-page">
      {/* Hero Banner */}
      <div className="register-hero">
        <h1>PLAYER REGISTRATION</h1>
        <p>Join {club?.name || 'ClubLink TT'} and start your football journey</p>
      </div>

      {/* Progress Steps */}
      <div className="progress-container">
        <div className="progress-steps">
          <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>
            <div className="step-number">1</div>
            <span>Player Info</span>
          </div>
          <div className={`progress-line ${step >= 2 ? 'active' : ''}`}></div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
            <div className="step-number">2</div>
            <span>Parent Info</span>
          </div>
          <div className={`progress-line ${step >= 3 ? 'active' : ''}`}></div>
          <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
            <div className="step-number">3</div>
            <span>Medical</span>
          </div>
          <div className={`progress-line ${step >= 4 ? 'active' : ''}`}></div>
          <div className={`progress-step ${step >= 4 ? 'active' : ''}`}>
            <div className="step-number">4</div>
            <span>Confirm</span>
          </div>
        </div>
      </div>

      {/* Form Container */}
      <div className="form-container">
        <form onSubmit={handleSubmit}>
          
          {/* Step 1: Player Information */}
          {step === 1 && (
            <div className="form-step">
              <h2>Player Information</h2>
              <p className="form-subtitle">Enter the player's details below</p>
              
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    name="playerFirstName"
                    value={formData.playerFirstName}
                    onChange={handleChange}
                    placeholder="Enter first name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    name="playerLastName"
                    value={formData.playerLastName}
                    onChange={handleChange}
                    placeholder="Enter last name"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date of Birth *</label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Gender *</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Age Group *</label>
                  <select
                    name="ageGroup"
                    value={formData.ageGroup}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select age group</option>
                    <option value="U8">U8 (Under 8)</option>
                    <option value="U10">U10 (Under 10)</option>
                    <option value="U12">U12 (Under 12)</option>
                    <option value="U14">U14 (Under 14)</option>
                    <option value="U16">U16 (Under 16)</option>
                    <option value="U18">U18 (Under 18)</option>
                    <option value="Tier1">Tier 1</option>
                    <option value="Tier2">Tier 2</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Preferred Position</label>
                  <select
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                  >
                    <option value="">Select position</option>
                    <option value="Goalkeeper">Goalkeeper</option>
                    <option value="Defender">Defender</option>
                    <option value="Midfielder">Midfielder</option>
                    <option value="Forward">Forward</option>
                    <option value="Any">Any Position</option>
                  </select>
                </div>
              </div>

              <div className="form-buttons">
                <button type="button" className="btn-next" onClick={nextStep}>
                  NEXT STEP →
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Parent/Guardian Information */}
          {step === 2 && (
            <div className="form-step">
              <h2>Parent/Guardian Information</h2>
              <p className="form-subtitle">Enter the parent or guardian's contact details</p>
              
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    name="parentFirstName"
                    value={formData.parentFirstName}
                    onChange={handleChange}
                    placeholder="Enter first name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    name="parentLastName"
                    value={formData.parentLastName}
                    onChange={handleChange}
                    placeholder="Enter last name"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter email address"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="e.g. 868-123-4567"
                    required
                  />
                </div>
              </div>

              <div className="form-group full-width">
                <label>Street Address *</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Enter street address"
                  required
                />
              </div>

              <div className="form-group full-width">
                <label>City/Town *</label>
                <select
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select city/town</option>
                  <option value="Port of Spain">Port of Spain</option>
                  <option value="San Fernando">San Fernando</option>
                  <option value="Chaguanas">Chaguanas</option>
                  <option value="Arima">Arima</option>
                  <option value="Point Fortin">Point Fortin</option>
                  <option value="Scarborough">Scarborough (Tobago)</option>
                  <option value="Couva">Couva</option>
                  <option value="Sangre Grande">Sangre Grande</option>
                  <option value="Tunapuna">Tunapuna</option>
                  <option value="Marabella">Marabella</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="form-buttons">
                <button type="button" className="btn-back" onClick={prevStep}>
                  ← BACK
                </button>
                <button type="button" className="btn-next" onClick={nextStep}>
                  NEXT STEP →
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Medical Information */}
          {step === 3 && (
            <div className="form-step">
              <h2>Medical Information</h2>
              <p className="form-subtitle">Important health and emergency contact details</p>
              
              <div className="form-group full-width">
                <label>Medical Conditions</label>
                <textarea
                  name="medicalConditions"
                  value={formData.medicalConditions}
                  onChange={handleChange}
                  placeholder="List any medical conditions (asthma, diabetes, etc.) or write 'None'"
                  rows="3"
                ></textarea>
              </div>

              <div className="form-group full-width">
                <label>Allergies</label>
                <textarea
                  name="allergies"
                  value={formData.allergies}
                  onChange={handleChange}
                  placeholder="List any allergies (food, medication, etc.) or write 'None'"
                  rows="3"
                ></textarea>
              </div>
              
              <div className="form-group full-width">
                <label>Prior Injuries</label>
                <textarea
                  name="priorInjuries"
                  value={formData.priorInjuries}
                  onChange={handleChange}
                  placeholder="List any prior injuries (sprains, fractures, surgeries, etc.) or write 'None'"
                  rows="3"
                ></textarea>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Emergency Contact Name *</label>
                  <input
                    type="text"
                    name="emergencyContact"
                    value={formData.emergencyContact}
                    onChange={handleChange}
                    placeholder="Full name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Emergency Contact Phone *</label>
                  <input
                    type="tel"
                    name="emergencyPhone"
                    value={formData.emergencyPhone}
                    onChange={handleChange}
                    placeholder="e.g. 868-123-4567"
                    required
                  />
                </div>
              </div>

              <div className="medical-notice">
                <div className="notice-icon">ℹ️</div>
                <p>Please ensure all medical information is accurate. This information will only be used in case of emergency during training or matches.</p>
              </div>

              <div className="form-buttons">
                <button type="button" className="btn-back" onClick={prevStep}>
                  ← BACK
                </button>
                <button type="button" className="btn-next" onClick={nextStep}>
                  NEXT STEP →
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Confirmation */}
          {step === 4 && (
            <div className="form-step">
              <h2>Confirm Registration</h2>
              <p className="form-subtitle">Review your information and accept the terms</p>
              
              {/* Summary */}
              <div className="summary-section">
                <h3>Player Details</h3>
                <div className="summary-grid">
                  <div className="summary-item">
                    <span className="summary-label">Name</span>
                    <span className="summary-value">{formData.playerFirstName} {formData.playerLastName}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Date of Birth</span>
                    <span className="summary-value">{formData.dateOfBirth}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Age Group</span>
                    <span className="summary-value">{formData.ageGroup}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Position</span>
                    <span className="summary-value">{formData.position || 'Not specified'}</span>
                  </div>
                </div>
              </div>

              <div className="summary-section">
                <h3>Contact Details</h3>
                <div className="summary-grid">
                  <div className="summary-item">
                    <span className="summary-label">Parent/Guardian</span>
                    <span className="summary-value">{formData.parentFirstName} {formData.parentLastName}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Email</span>
                    <span className="summary-value">{formData.email}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Phone</span>
                    <span className="summary-value">{formData.phone}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Location</span>
                    <span className="summary-value">{formData.city}</span>
                  </div>
                </div>
              </div>

              {/* Terms */}
              <div className="terms-section">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="agreeTerms"
                    checked={formData.agreeTerms}
                    onChange={handleChange}
                    required
                  />
                  <span className="checkmark"></span>
                  I agree to the <a href="#terms">Terms and Conditions</a> and <a href="#privacy">Privacy Policy</a> *
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="agreePhotos"
                    checked={formData.agreePhotos}
                    onChange={handleChange}
                  />
                  <span className="checkmark"></span>
                  I consent to photos/videos of my child being used for {club?.name || 'ClubLink TT'} promotional materials
                </label>
              </div>

              {/* Fee Notice */}
              <div className="fee-notice">
                <h4>Registration Fee</h4>
                {/* TODO: Update fee amount when confirmed by club */}
                <div className="fee-amount">TT$ 200</div>
                <p>Payment can be made online after registration or at the club office.</p>
              </div>

              <div className="form-buttons">
                <button type="button" className="btn-back" onClick={prevStep} disabled={submitting}>
                  ← BACK
                </button>
                <button 
                  type="submit" 
                  className="btn-submit"
                  disabled={!formData.agreeTerms || submitting}
                >
                  {submitting ? 'SUBMITTING...' : 'SUBMIT REGISTRATION'}
                </button>
              </div>
            </div>
          )}

        </form>
      </div>
    </div>
  );
}

export default Register;